"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";

// WebMCP (https://webmachinelearning.github.io/webmcp/): exposes read-only site
// tools to an AI agent running in the visitor's browser. A no-op in browsers
// without the API. Tools reuse the public JSON API and the markdown renditions
// (Accept: text/markdown), so they see exactly what the pages show.

type ToolResult = { content: { type: "text"; text: string }[] };
type Tool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => Promise<ToolResult>;
};
type ModelContext = {
  registerTool: (tool: Tool, options?: { signal?: AbortSignal }) => Promise<void> | void;
};

const text = (value: unknown): ToolResult => ({
  content: [{ type: "text", text: typeof value === "string" ? value : JSON.stringify(value, null, 2) }],
});

// Same-origin paths only, so a tool can't be pointed at another site.
function sitePath(input: unknown): string {
  const path = typeof input === "string" ? input.trim() : "";
  if (!path.startsWith("/") || path.startsWith("//")) throw new Error("path must start with /");
  return path;
}

async function getJson(path: string) {
  const res = await fetch(path, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`${path} returned ${res.status}`);
  return res.json();
}

export default function WebMcpTools() {
  const router = useRouter();

  useEffect(() => {
    const ctx =
      (document as unknown as { modelContext?: ModelContext }).modelContext ??
      (navigator as unknown as { modelContext?: ModelContext }).modelContext;
    if (!ctx?.registerTool) return;

    const tools: Tool[] = [
      {
        name: "list_blog_posts",
        description: "List Josh Wood's published blog posts with title, date, excerpt and URL.",
        inputSchema: { type: "object", properties: {} },
        async execute() {
          const posts: { title: string; slug: string; excerpt: string; publishedAt?: string; createdAt: string }[] =
            await getJson("/api/blogs");
          return text(
            posts.map((p) => ({
              title: p.title,
              date: (p.publishedAt ?? p.createdAt)?.slice(0, 10),
              excerpt: p.excerpt,
              url: `/blog/${p.slug}`,
            }))
          );
        },
      },
      {
        name: "list_projects",
        description: "List Josh Wood's portfolio projects with a one-line description, tech stack and URL.",
        inputSchema: { type: "object", properties: {} },
        async execute() {
          const projects: { title: string; slug: string; description: string; tech?: string[] }[] =
            await getJson("/api/projects");
          return text(
            projects.map((p) => ({ title: p.title, description: p.description, tech: p.tech, url: `/projects/${p.slug}` }))
          );
        },
      },
      {
        name: "list_certifications",
        description: "List Josh Wood's professional certifications.",
        inputSchema: { type: "object", properties: {} },
        async execute() {
          const certs: { name: string; issuer: string; issueDate: string; expiryDate?: string; credentialUrl?: string }[] =
            await getJson("/api/certifications");
          return text(
            certs.map((c) => ({
              name: c.name,
              issuer: c.issuer,
              issued: c.issueDate,
              expires: c.expiryDate || undefined,
              verify: c.credentialUrl || undefined,
            }))
          );
        },
      },
      {
        name: "read_page",
        description:
          "Read a page of joshwood.ca as markdown. Supported paths: /, /blog, /blog/{slug}, /projects, /projects/{slug}.",
        inputSchema: {
          type: "object",
          properties: { path: { type: "string", description: "Site path, e.g. /blog/some-post" } },
          required: ["path"],
        },
        async execute(input) {
          const path = sitePath(input.path);
          const res = await fetch(path, { headers: { Accept: "text/markdown" } });
          if (!res.ok || !res.headers.get("content-type")?.includes("text/markdown")) {
            throw new Error(`No markdown version of ${path}`);
          }
          return text(await res.text());
        },
      },
      {
        name: "go_to_page",
        description: "Navigate the visitor's browser to a page on joshwood.ca, e.g. /contact or /blog/{slug}.",
        inputSchema: {
          type: "object",
          properties: { path: { type: "string", description: "Site path starting with /" } },
          required: ["path"],
        },
        async execute(input) {
          const path = sitePath(input.path);
          router.push(path);
          return text(`Navigated to ${path}`);
        },
      },
    ];

    const controller = new AbortController();
    for (const tool of tools) {
      try {
        void Promise.resolve(ctx.registerTool(tool, { signal: controller.signal })).catch(() => {});
      } catch {
        // An older/partial implementation; skip this tool rather than break the page.
      }
    }
    return () => controller.abort();
  }, [router]);

  return null;
}
